import { Outlet, NavLink, useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { LayoutDashboard, FolderKanban, Users, CalendarClock, LogOut, BarChart3, SlidersHorizontal, Newspaper, Mail, Award, Scale, ShieldAlert, Rss, Film, Share2, Link2, Bell } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";
import { isSubAdmin, firstAllowedRoute } from "../../lib/adminSections";
import { api } from "../../lib/api";
import Logo from "../../components/Logo";
import UserAvatar from "../../components/UserAvatar";
import LegalStrip from "../../components/LegalStrip";
import ThemeSection from "../../components/ThemeToggle";

const NAV = [
  { to: "/admin", key: "adm_nav_dash", icon: LayoutDashboard, end: true, perm: "dashboard" },
  { to: "/admin/analyse", key: "adm_nav_traffic", icon: BarChart3, perm: "analytics" },
  { to: "/admin/projets", key: "adm_nav_requests", icon: FolderKanban, perm: "projects" },
  { to: "/admin/groupes", key: "Projets par utilisateur", icon: Users, perm: "projects" },
  { to: "/admin/utilisateurs", key: "adm_nav_users", icon: Users, perm: "users" },
  { to: "/admin/liens", key: "Partenaires", icon: Link2, perm: "partners" },
  { to: "/admin/ambassadeurs", key: "adm_nav_ambassadors", icon: Award, perm: "ambassadors" },
  { to: "/admin/comite", key: "adm_nav_committee", icon: Scale, perm: "committee" },
  { to: "/admin/semaine", key: "adm_nav_week", icon: CalendarClock, perm: "weekly" },
  { to: "/admin/presse", key: "adm_nav_press", icon: Newspaper, perm: "press" },
  { to: "/admin/blog", key: "nav_blog", icon: Rss, perm: "blog" },
  { to: "/admin/videos", key: "Vidéos", icon: Film, perm: "videos" },
  { to: "/admin/reseaux", key: "Réseaux sociaux", icon: Share2, perm: "socials" },
  { to: "/admin/reglages", key: "adm_nav_site", icon: SlidersHorizontal, perm: "settings" },
  { to: "/admin/reglages-email", key: "adm_nav_email", icon: Mail, perm: "email" },
  { to: "/admin/securite", key: "adm_nav_security", icon: ShieldAlert, perm: "security" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { tf: t } = useLang();
  const nav = useNavigate();
  const loc = useLocation();

  const restricted = isSubAdmin(user);
  const perms = restricted ? user.admin_permissions : null;
  const visibleNav = restricted ? NAV.filter((n) => perms.includes(n.perm)) : NAV;

  // Garde de route : un sous-admin ne peut pas ouvrir manuellement une section interdite.
  if (restricted) {
    const match = NAV.find((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to)));
    if (!perms.length) {
      return (
        <div className="grain flex min-h-screen items-center justify-center bg-void p-6">
          <p className="max-w-md text-center text-sm text-ink-secondary" data-testid="admin-no-permission">
            Aucune section ne vous est attribuée. Contactez le super administrateur.
          </p>
        </div>
      );
    }
    if (match && !perms.includes(match.perm)) {
      return <Navigate to={firstAllowedRoute(user)} replace />;
    }
  }

  return (
    <div className="grain flex min-h-screen bg-void">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-white/10 bg-surface lg:flex">
        <Link to="/" className="flex items-center gap-2 border-b border-white/10 px-6 py-5"><Logo size={26} /></Link>
        <div className="border-b border-white/10 px-6 py-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-cyan">{restricted ? "Sous-administrateur" : t("adm_super")}</div>
          <div className="mt-1 truncate text-xs text-ink-secondary" data-testid="admin-identity">{user?.email}</div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-4" data-testid="admin-nav">
          {visibleNav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.to.split("/").pop() || "dash"}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 text-sm transition-all ${isActive ? "border-l-2 border-cyan bg-cyan/5 text-cyan" : "border-l-2 border-transparent text-ink-secondary hover:text-white"}`}>
              <n.icon size={18} /> {t(n.key)}
            </NavLink>
          ))}
        </nav>
        <ThemeSection testid="admin-theme" />
        <button onClick={() => { logout(); nav("/"); }} data-testid="admin-logout" className="flex items-center gap-3 border-t border-white/10 px-6 py-4 text-sm text-ink-secondary hover:text-white">
          <LogOut size={18} /> {t("adm_logout")}
        </button>
      </aside>
      <div className="min-w-0 flex-1 lg:pl-64">
        {/* Compte connecté (photo ou initiales) + déconnexion rapide, en haut à droite */}
        <div className="fixed right-4 top-4 z-40 hidden items-center gap-2.5 lg:flex">
          <AdminNotifications />
          <div title={`${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email} data-testid="admin-top-identity"
            className="rounded-full border border-white/20 bg-surface/90 p-0.5 backdrop-blur-md">
            <UserAvatar user={user} size={36} testid="admin-top-avatar" />
          </div>
          <button onClick={() => { logout(); nav("/"); }} data-testid="admin-logout-top" title="Déconnexion" aria-label="Déconnexion"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-surface/90 text-ink-secondary backdrop-blur-md transition-all duration-150 hover:border-red-400/60 hover:bg-red-500/10 hover:text-red-400 active:scale-95">
            <LogOut size={17} />
          </button>
        </div>
        <div className="flex items-center gap-1 overflow-x-auto border-b border-white/10 bg-surface px-4 py-3 lg:hidden">
          {visibleNav.map((n) => <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-m-${n.to.split("/").pop() || "dash"}`} className={({ isActive }) => `p-2 ${isActive ? "text-cyan" : "text-ink-secondary"}`}><n.icon size={19} /></NavLink>)}
          <div className="ml-auto flex items-center gap-1">
            <button onClick={() => { logout(); nav("/"); }} className="p-2 text-ink-secondary"><LogOut size={19} /></button>
          </div>
        </div>
        <div className="p-5 sm:p-8"><Outlet /></div>
        <LegalStrip />
      </div>
    </div>
  );
}

// Cloche de notifications admin : confirme que les envois d'e-mails sont bien partis
// (résultat d'expédition) et toute autre notification système. Sondage toutes les 30 s.
function AdminNotifications() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const load = () => api.get("/admin/notifications").then((r) => setData(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const toggle = async () => {
    const opening = !open;
    setOpen(opening);
    if (opening && data.unread > 0) {
      try { await api.post("/admin/notifications/read"); } catch { /* silencieux */ }
      setData((d) => ({ ...d, unread: 0, items: d.items.map((i) => ({ ...i, read: true })) }));
    }
  };

  return (
    <div className="relative">
      <button onClick={toggle} data-testid="admin-notif-bell" title="Notifications" aria-label="Notifications"
        className="relative flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-surface/90 text-ink-secondary backdrop-blur-md transition-all duration-150 hover:border-cyan/50 hover:text-cyan active:scale-95">
        <Bell size={17} />
        {data.unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-cyan px-1 text-[10px] font-700 text-void" data-testid="admin-notif-badge">
            {data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-12 w-80 rounded-xl border border-white/10 bg-elevated p-2 shadow-[0_8px_30px_rgba(0,0,0,0.5)]" data-testid="admin-notif-panel">
          <div className="px-3 py-2 text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Notifications</div>
          <div className="max-h-80 overflow-y-auto">
            {data.items.map((n) => (
              <div key={n.id} className={`rounded-lg px-3 py-2.5 ${!n.read ? "bg-cyan/5" : ""}`} data-testid={`admin-notif-${n.id}`}>
                <div className="text-sm font-600 text-white">{n.title}</div>
                <div className="mt-0.5 text-xs leading-relaxed text-ink-secondary">{n.message}</div>
                <div className="mt-1 text-[10px] text-ink-tertiary">{n.at ? new Date(n.at).toLocaleString("fr-FR") : ""}</div>
              </div>
            ))}
            {data.items.length === 0 && (
              <p className="px-3 py-6 text-center text-xs text-ink-tertiary" data-testid="admin-notif-empty">Aucune notification pour le moment.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
